// ===========================================
// Merge-field chips — insert a token at the cursor
// ===========================================
//
// One row of chips, shared by every editor that personalises text. EXTRACTED
// from `CustomEmailEditorPage` (2026-09-17) when email sequences needed the
// same row with a DIFFERENT field list — copying it would have left two
// implementations to keep in step, and this repo already has one stale copy of
// this idea (`TemplateEditorDialog`, which is onClick-only, replaces the
// selection, and labels chips with the raw placeholder).
//
// ⚠️ The field LIST stays with the caller, deliberately. Each editor's list is
// tied to a different backend authority — custom email refuses
// `{{studentName}}` because a campaign targets contacts who may have several
// children, while a sequence enrolment is per student and offers it. A shared
// default here would eventually be wrong for one of them.

import { Chip, Stack, Typography } from '@mui/material';

export interface MergeFieldOption {
  label: string;
  placeholder: string;
}

/**
 * ⚠️ GENERIC OVER THE TARGET, not `target: string`.
 *
 * Widening to `string` looks harmless and does not compile: a caller whose
 * handler takes `'subject' | 'preheader' | 'body'` cannot be assigned to one
 * declared to accept any `string` (the parameter is contravariant), so every
 * existing call site errors. Loosening the CALLER to match would throw away the
 * exhaustiveness its own switch depends on.
 *
 * `T` lets each editor keep its own closed union and still share this row.
 */
export interface MergeFieldChipsProps<T extends string> {
  /** The fields to offer. Kept caller-side — see the note above. */
  fields: MergeFieldOption[];
  /** Which control the chips insert into; echoed into the `data-testid` below. */
  target: T;
  onInsert: (e: React.MouseEvent, placeholder: string, target: T) => void;
  disabled?: boolean;
  caption?: string;
}

/** One row of insert-at-cursor chips, shared by the subject, preheader and body. */
export default function MergeFieldChips<T extends string>({
  fields,
  target,
  onInsert,
  disabled,
  caption = 'Personalise:',
}: MergeFieldChipsProps<T>) {
  return (
    <Stack
      direction="row"
      spacing={0.5}
      flexWrap="wrap"
      useFlexGap
      sx={{ mt: 0.5 }}
      /* Lets a test address THIS row by identity. Indexing the identical rows by
         render order silently retargets if one is added or reordered — and the
         test would keep passing while asserting the wrong field. */
      data-testid={`merge-field-chips-${target}`}
    >
      <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, alignSelf: 'center' }}>
        {caption}
      </Typography>
      {fields.map((field) => (
        <Chip
          key={`${target}-${field.placeholder}`}
          label={field.label}
          size="small"
          variant="outlined"
          color="primary"
          disabled={disabled}
          clickable
          /* ⚠️ BOTH handlers, deliberately.
             `onMouseDown` + preventDefault is what preserves the caret: a plain
             click moves focus to the chip first, and the input's selectionStart
             is then gone, so the token lands at the end instead of the cursor.
             But `onMouseDown` ALONE leaves the chip keyboard-dead — MUI only
             renders role="button"/tabIndex from `clickable`/`onClick`, so Enter
             and Space would do nothing and a keyboard-only admin could not
             personalise an email at all.
             So: mouse users get caret-accurate insertion, keyboard users get a
             real button. The caller's insert handler must no-op on the repeated
             mouse event so the two cannot double-insert — see
             `utils/mergeFieldInsert.ts`. */
          onMouseDown={(e) => onInsert(e, field.placeholder, target)}
          onClick={(e) => onInsert(e, field.placeholder, target)}
          aria-label={`Insert ${field.label.toLowerCase()} merge field`}
          sx={{ cursor: 'pointer', height: 24, fontSize: '0.75rem' }}
        />
      ))}
    </Stack>
  );
}
